import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Typography, Stack, Card, CardContent, LinearProgress, Box,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { CustomDialogBox } from '../../../../components/CustomDialogBox';
import palette from '../../../../theme/palette';
import {
  crudOperations, customEntityIcons, customEntityKeys, eventSourceType,
} from '../../constants';

function DialogContent({ loading, data }) {
  const parseOps = (item) => {
    if (Array.isArray(item)) return item;
    if (typeof item === 'string') {
      const parsed = JSON.parse(item);
      return Array.isArray(parsed) ? parsed : [];
    }
    return [];
  };

  const getSourceType = (sourceType) => {
    const defaultType = eventSourceType[0].name;
    if (!sourceType) return defaultType;
    const found = eventSourceType.find((e) => e.value === sourceType);
    return found.name || defaultType;
  };

  if (loading) {
    return <Box height={200}><LinearProgress /></Box>;
  }

  return (
    <Stack gap={1}>
      {data.map((item) => {
        const isNested = item.type === customEntityKeys.NESTED_FEATURE;
        const label = item.custom_entity_label || item.nested_feature_name;
        const icon = customEntityIcons[item.custom_entity_type || item.type];
        const ops = parseOps(item.operation_type);
        const hasAllOps = ops.length > 0 && ops.length === crudOperations.length;

        return (
          <Card
            variant="outlined"
            sx={{
              borderRadius: 1,
              p: 1.5,
              height: '100%',
              boxShadow: 'rgba(0, 0, 0, 0.04) 0px 3px 5px',
            }}
          >
            <CardContent sx={{ px: '4px', py: isNested ? '8px !important' : '0 !important' }}>
              {/* Line 1: Name + Icon */}
              <Box display="inline">
                {icon && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    flexDirection: 'row',
                    position: 'relative',
                    top: '2px',
                    mr: 0.5,
                  }}
                >
                  {React.cloneElement(icon, {
                    style: { fontSize: '16px', color: palette.secondary.main },
                  })}
                </Box>
                )}

                <Typography
                  variant="subtitle1"
                  sx={{
                    display: 'inline',
                    fontWeight: 600,
                    whiteSpace: 'normal',
                    wordBreak: 'break-all',
                    overflowWrap: 'break-word',
                    lineHeight: 1.3,
                  }}
                >
                  {label || '- - -'}
                </Typography>
              </Box>

              {/* Line 2: Type | Operations */}
              <Typography variant="body2" mt={0.5}>
                {item.custom_entity_type || item.type}
                {ops.length > 0 && ' - '}
                {hasAllOps ? 'All Events' : ops.join(' | ')}
              </Typography>

              {/* Line 3: Sources on left, Package on right */}
              {!isNested && (
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                <Typography variant="body2">
                  Source:
                  {' '}
                  {getSourceType(item.event_source_type?.[0])}
                </Typography>
                {!isNested && item.package_label && (
                <Typography variant="body2" color="text.secondary">
                  {item.package_label}
                </Typography>
                )}
              </Stack>
              )}
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );
}

export default function FeatureLabelModal(props) {
  const {
    open,
    handleClose,
    label,
    permissions,
  } = props;

  const navigate = useNavigate();
  const [selectedCustomEntity, setSelectedCustomEntity] = useState([]);
  const [selectedNestedFeatures, setSelectedNestedFeatures] = useState([]);
  const featureData = useSelector((state) => state.featureDetailList);

  const handleClickEdit = () => {
    const id = featureData?.featureId;
    if (id) navigate(`/feature-manager/update-feature/${id}`);
  };

  useEffect(() => {
    if (featureData?.customEntity?.length) {
      const filterData = [];
      featureData.customEntity.forEach((feature) => {
        const cloneCustomData = { ...feature };
        cloneCustomData.operation_type = JSON.parse(feature.operation_type);
        filterData.push(cloneCustomData);
        setSelectedCustomEntity(filterData);
      });
    } else setSelectedCustomEntity([]);

    if (featureData?.nestedFeatures?.length) {
      const nestedFeatureInfo = featureData.nestedFeatures;
      setSelectedNestedFeatures(nestedFeatureInfo);
    } else setSelectedNestedFeatures([]);
  }, [featureData]);

  const mergedData = [...selectedNestedFeatures, ...selectedCustomEntity];

  return (
    <CustomDialogBox
      table
      title={label}
      open={open}
      handleClose={handleClose}
      content={<DialogContent loading={featureData.loading} data={mergedData} />}
      actions={permissions.write && { title: 'Edit Feature', onClick: handleClickEdit }}
    />
  );
}
